import type { IFetchRequest, ILog } from '@monofe/util';
import type { IMessageInstantiator } from '../../message/interface/instantiator';
import type { IChannelID, IRegexSerializer } from '../../util';

function addCacheFirst(
    dependencies: {
        log: ILog;
        regexParser: IRegexSerializer;
        message: IMessageInstantiator;
        worker: ServiceWorker;
    },
    matcher: RegExp,
    maxAge: number,
    maxEntries: number,
    cacheName: string,
    cacheQueryOptions?: Partial<CacheQueryOptions>,
    fetchOptions?: Partial<RequestInit>,
): void {
    dependencies.log.info(`Requesting cache-first handler: ${cacheName}`);

    const message = dependencies.message.message('AddCacheFirst').construct(
        {
            matcher,
            maxAge,
            maxEntries,
            cacheName,
            cacheQueryOptions,
            fetchOptions,
        },
        {
            regexParser: dependencies.regexParser,
        },
    );

    dependencies.worker.postMessage(message);
}

function addOAuthTokenCache(
    dependencies: {
        log: ILog;
        regexParser: IRegexSerializer;
        message: IMessageInstantiator;
        worker: ServiceWorker;
    },
    pathSuffix: string,
): void {
    dependencies.log.info(`Requesting oauthTokenCache: ${pathSuffix}`);

    const message = dependencies.message.message('AddOAuthTokenCache').construct(
        {
            pathSuffix,
        },
        {},
    );

    dependencies.worker.postMessage(message);
}

function addZipPathFetchResponder(
    dependencies: {
        log: ILog;
        regexParser: IRegexSerializer;
        message: IMessageInstantiator;
        worker: ServiceWorker;
    },
    zipPath: string,
    relativeRespondPath: string,
): void {
    dependencies.log.info(`Requesting zipPathResponder: ${zipPath}`);

    const message = dependencies.message.message('AddZipPathRelativeResponder').construct(
        {
            zipPath,
            relativeRespondPath,
        },
        {},
    );

    dependencies.worker.postMessage(message);
}

function addHeader(
    dependencies: {
        log: ILog;
        regexParser: IRegexSerializer;
        message: IMessageInstantiator;
        worker: ServiceWorker;
    },
    matcher: RegExp,
    headerKey: string,
    headerVal: string,
    expiration: Date,
): void {
    dependencies.log.info(`Requesting addHeader: ${headerKey}:${headerVal}`);

    const message = dependencies.message.message('AddHeader').construct(
        {
            matcher,
            headerKey,
            headerVal,
            expiration,
        },
        {
            regexParser: dependencies.regexParser,
        },
    );

    dependencies.worker.postMessage(message);
}

function fetchToCache(
    dependencies: {
        log: ILog;
        regexParser: IRegexSerializer;
        message: IMessageInstantiator;
        worker: ServiceWorker;
    },
    requests: IFetchRequest[],
    cacheName?: string,
    port?: IChannelID,
): void {
    dependencies.log.verbose(
        `Request fetch to cache download: ${JSON.stringify(requests.map(req => req.url))}`,
    );

    const message = dependencies.message.message('FetchToCache').construct(
        {
            requests,
            cacheName,
            callbackChannel: port,
        },
        {},
    );

    dependencies.worker.postMessage(message);
}

function keepAlive(dependencies: {
    log: ILog;
    regexParser: IRegexSerializer;
    message: IMessageInstantiator;
    worker: ServiceWorker;
}): void {
    const message = dependencies.message.message('AddKeepAliveMessageResponder').construct({}, {});

    dependencies.worker.postMessage(message);
}
export default Object.freeze({
    addCacheFirst,
    addOAuthTokenCache,
    addZipPathFetchResponder,
    addHeader,
    fetchToCache,
    keepAlive,
});
